import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';

import customers from '@/api/customers';
import { normalizeChild, normalizeChildren, normalizeOutlet } from '@/helper/parentNormalize';

export const getCustomer = createAsyncThunk('auth/setCustomer', async (params) => {
	const { getCustomers } = customers();
	const response = await getCustomers(params.params, params.id);
	return response.data;
});

function resolveLoginCustomerId(data) {
	const loginCustomer = data.user?.customer ?? null;
	if (loginCustomer?.id != null) return Number(loginCustomer.id);

	const children = normalizeChildren(data.children);
	if (children.length === 1) return children[0].id;
	return null;
}

function childById(children, id) {
	if (id == null) return null;
	return children.find((c) => Number(c.id) === Number(id)) || null;
}

/** Derive selectedCustomer + selectedOutlet from selectedCustomerId + children. */
export function applySelection(state, preferredId) {
	const children = state.children || [];
	let id = preferredId != null ? Number(preferredId) : state.selectedCustomerId;

	if (id != null && !childById(children, id)) {
		id = null;
	}

	if (id == null && children.length === 1) {
		id = children[0].id;
	}

	const selected = childById(children, id);
	state.selectedCustomerId = selected ? selected.id : null;
	state.selectedCustomer = selected;

	const outlet =
		normalizeOutlet(selected?.outlet) ||
		normalizeOutlet(state.parentOutlet) ||
		null;

	state.selectedOutlet = outlet || false;
	if (outlet) {
		Cookies.set('jt-o', JSON.stringify(outlet));
	} else {
		Cookies.remove('jt-o');
	}
}

const authSlice = createSlice({
	name: 'auth',
	initialState: {
		isAuthenticated: false,
		isSuperUser: false,
		isGroups: [],
		key: Cookies.get('wbAKey'),
		user: Cookies.get('wbAUser') ? JSON.parse(Cookies.get('wbAUser')) : false,
		selectedOutlet: Cookies.get('jt-o')
			? JSON.parse(Cookies.get('jt-o'))
			: Cookies.get('wbA-o')
				? JSON.parse(Cookies.get('wbA-o'))
				: false,
		selectedCustomer: null,
		children: [],
		accountType: null,
		selectedCustomerId: null,
		/** Parent-level outlet from me/ when no child is selected */
		parentOutlet: null,
		childrenLoaded: false,
		/** PA-07 will set true during checkout / top-up / cancel submit */
		switchingLocked: false,

		isSSO: false,
	},
	reducers: {
		setLogginSSO(state, action) {
			const currState = state;
			const currSSO = false;

			const data = action.payload;
			const { token, user } = data;
			const userWithoutSidebar = { ...user, sidebar_items: null };
			const lang = localStorage.getItem('wbAlang');
			const langId = localStorage.getItem('wbAlangId');

			let expiryTime = 0.5;
			let selectedToken = token;
			if (currSSO) {
				expiryTime = token.expires_in / 86400;
				selectedToken = token.access_token;
			}

			if ((!lang || !langId) && user.client.languages.length > 0) {
				localStorage.setItem('wbALang', user.client.languages[0].abbreviation);
				localStorage.setItem('wbALangId', user.client.languages[0].id);
			} else {
				localStorage.setItem('wbALang', 'en');
				localStorage.setItem('wbALangId', 1);
			}
			localStorage.setItem('wbAKey', selectedToken);
			localStorage.setItem('wbAUser', JSON.stringify(userWithoutSidebar));

			if (!user?.is_superuser) {
				Cookies.set('wbAaClient', user.client.id, { expires: expiryTime });
				if (user.sites && user.sites.length > 0) {
					Cookies.set('wbASite', user.sites[0].id, { expires: expiryTime });
					localStorage.setItem('wbASiteId', user.sites[0].id);
				}
			}

			Cookies.set('wbAKey', selectedToken, { expires: expiryTime });
			Cookies.set('wbAUser', JSON.stringify(userWithoutSidebar), { expires: expiryTime });
			Cookies.set('wbAClient', JSON.stringify(user.client), { expires: expiryTime });

			if (user.client && selectedToken && user) {
				currState.client = user.client;
				currState.key = selectedToken;
				// Keep account profile only — do not replace with a student record
				currState.user = user;
				currState.isAuthenticated = true;
				currState.isSuperUser = user.is_superuser;
				if (!user.is_superuser && user.groups.length) {
					currState.isGroups = user.groups?.map((gr) => gr.id);
				}
			}

			const children = normalizeChildren(
				data.children ?? (data.user?.customer ? [data.user.customer] : [])
			);
			currState.children = children;
			currState.accountType =
				data.account_type ?? (data.user?.customer ? 'legacy' : 'parent');
			currState.parentOutlet =
				normalizeOutlet(data.parentOutlet) ||
				normalizeOutlet(data.outlet) ||
				null;
			currState.childrenLoaded = true;

			applySelection(currState, resolveLoginCustomerId(data));
		},
		setParentMe(state, action) {
			const currState = state;
			const me = action.payload;
			const children = normalizeChildren(me?.children);
			currState.children = children;
			currState.accountType = me?.account_type ?? currState.accountType ?? 'parent';
			currState.parentOutlet =
				normalizeOutlet(me?.outlet) || currState.parentOutlet || null;
			currState.childrenLoaded = true;

			const preferredId = currState.selectedCustomerId;
			applySelection(currState, preferredId);
		},
		setSelectedCustomerId(state, action) {
			const id = action.payload;
			applySelection(state, id);
		},
		clearSelectedChild(state) {
			applySelection(state, null);
		},
		setSwitchingLocked(state, action) {
			state.switchingLocked = Boolean(action.payload);
		},
		setAuthenticated(state) {
			const currState = state;
			const objState = current(currState);
			if (objState.key && objState.user) {
				currState.isAuthenticated = true;
				currState.isSuperUser = objState.user.is_superuser;

				if (!objState.user.is_superuser && objState.user.groups.length) {
					currState.isGroups = objState.user.groups?.map((gr) => gr.id);
				}
			}
		},
		setLogout(state) {
			const currState = state;
			currState.key = false;
			currState.user = false;
			currState.isAuthenticated = false;
			currState.selectedCustomer = null;
			currState.selectedCustomerId = null;
			currState.selectedOutlet = false;
			currState.children = [];
			currState.accountType = null;
			currState.parentOutlet = null;
			currState.childrenLoaded = false;
			currState.switchingLocked = false;
			Cookies.remove('wbAKey', { path: '/' });
			Cookies.remove('wbAUser');
			Cookies.remove('jt-o');
			localStorage.removeItem('wbAKey');
			localStorage.removeItem('wbAUser');
			localStorage.removeItem('isSSO');
			localStorage.removeItem('groupClient');
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getCustomer.fulfilled, (state, action) => {
			const payload = action.payload;
			if (!payload) return;

			const normalized = normalizeChild(payload);
			if (!normalized) return;

			// Ignore late responses for a different child
			if (
				state.selectedCustomerId != null &&
				Number(normalized.id) !== Number(state.selectedCustomerId)
			) {
				return;
			}

			state.selectedCustomer = {
				...(state.selectedCustomer || {}),
				...normalized,
			};
			state.children = (state.children || []).map((c) =>
				Number(c.id) === Number(normalized.id) ? { ...c, ...normalized } : c
			);

			const outlet = normalizeOutlet(normalized.outlet);
			if (outlet) {
				state.selectedOutlet = outlet;
				Cookies.set('jt-o', JSON.stringify(outlet));
			}

			// Legacy screens still read user.customer — keep that shape only for legacy
			if (state.accountType === 'legacy' && state.user) {
				state.user = { ...state.user, customer: payload };
			}
		});
	},
});

export const authActions = authSlice.actions;
export default authSlice;
